import { useEffect } from "react";
import {
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  PASTE_COMMAND,
  SKIP_SCROLL_INTO_VIEW_TAG,
  type BaseSelection,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

export type PasteResult = false | Promise<string | null>;

interface PasteInterceptorPluginProps {
  onPaste?: (text: string) => PasteResult;
}

export const PasteInterceptorPlugin = (
  props: PasteInterceptorPluginProps,
): null => {
  const [editor] = useLexicalComposerContext();

  useEffect((): (() => void) | undefined => {
    if (!props.onPaste) {
      return undefined;
    }
    return editor.registerCommand<ClipboardEvent | InputEvent | KeyboardEvent>(
      PASTE_COMMAND,
      (event): boolean => {
        if (!("clipboardData" in event) || !event.clipboardData) {
          return false;
        }
        const text: string = event.clipboardData.getData("text") ?? "";
        const result: PasteResult = props.onPaste!(text);
        if (result === false) {
          return false;
        }
        event.preventDefault();

        const liveSelection: BaseSelection | null = $getSelection();
        const savedSelection: BaseSelection | null = liveSelection
          ? liveSelection.clone()
          : null;

        result.then((cleaned: string | null): void => {
          if (cleaned === null) {
            return;
          }
          editor.update(
            (): void => {
              if (savedSelection) {
                $setSelection(savedSelection.clone());
              }
              const selection: BaseSelection | null = $getSelection();
              if ($isRangeSelection(selection)) {
                selection.insertText(cleaned);
              }
            },
            { tag: SKIP_SCROLL_INTO_VIEW_TAG },
          );
          editor.focus();
        });
        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, props.onPaste]);

  return null;
};
